import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Building, CheckCircle, Star, ArrowRight, Home, Car, School, Hospital, Shield, Zap } from 'lucide-react';

const FaisalHills = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const highlights = [
    { icon: CheckCircle, title: 'RDA Approved', description: 'No Objection Certificate from Rawalpindi Development Authority' },
    { icon: Building, title: '11,823 Kanals', description: 'Vast RDA-approved area for residential and commercial development' },
    { icon: MapPin, title: 'Prime Location', description: 'Strategic location on GT Road near Taxila, Islamabad & Rawalpindi' },
    { icon: Shield, title: 'Gated Community', description: 'Secure, well-maintained environment with 24/7 security' },
    { icon: Car, title: 'Easy Accessibility', description: 'Connected via M-1 Motorway, Margalla Avenue & GT Road' },
    { icon: Star, title: 'Reputed Developer', description: 'Developed by Faisal Town Group (Zedem International)' }
  ];

  const blocks = [
    {
      name: 'Block A',
      type: 'Commercial Hub',
      description: 'Core business district with 225ft main boulevard',
      features: ['Commercial Complex', 'Wide Streets (40-110ft)', 'Business Infrastructure', 'High Investment Returns'],
      gradient: 'from-blue-600 to-blue-700'
    },
    {
      name: 'Block B',
      type: 'Residential',
      description: 'Peaceful family living with green parks and open spaces',
      features: ['Green Parks', 'Scenic Views', 'Affordable Prices', 'Family Environment'],
      gradient: 'from-emerald-600 to-emerald-700'
    },
    {
      name: 'Block C',
      type: 'Premium Residential',
      description: 'High-end area near future CPEC route on M-1 highway',
      features: ['CPEC Route Access', 'Premium Location', 'High Demand', 'Investment Potential'],
      gradient: 'from-purple-600 to-purple-700'
    },
    {
      name: 'Block D',
      type: 'Affordable Living',
      description: 'Modern residential facilities at economical prices',
      features: ['International Infrastructure', 'Affordable Cost', 'Modern Facilities', 'Great Location'],
      gradient: 'from-amber-600 to-amber-700'
    }
  ];

  const amenities = [
    { icon: Hospital, title: 'Healthcare Facilities', description: 'Modern hospitals and clinics with immediate medical care' },
    { icon: School, title: 'Educational Institutes', description: 'Root Millennium School and other quality educational institutions' },
    { icon: Building, title: 'Commercial Downtown', description: 'Vibrant commercial district with shops, offices, and businesses' },
    { icon: Shield, title: 'Gated Community', description: '24/7 security with monitored entry and exit points' },
    { icon: Zap, title: 'Utilities', description: '24/7 electricity, water, and gas supply' },
    { icon: Home, title: 'Mosques', description: 'Beautiful mosques for daily prayers and spiritual gatherings' }
  ];

  const nearbyLandmarks = [
    { name: 'Rawalpindi', distance: '21 minutes', type: 'City' },
    { name: 'Islamabad', distance: '22 minutes', type: 'Capital' },
    { name: 'Taxila', distance: '5 minutes', type: 'Historical City' },
    { name: 'Multi Gardens B-17', distance: '2 minutes', type: 'Housing Society' },
    { name: 'Islamabad Airport', distance: '25 minutes', type: 'Airport' },
    { name: 'HITEC University', distance: '15 minutes', type: 'University' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden pt-32">
        {/* Hero Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/hero bg.png" 
            alt="Faisal Hills Hero Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-90" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center mb-6 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Star className="h-4 w-4 text-blue-400 mr-2" />
              <span className="text-white text-sm font-medium">RDA Approved Housing Society</span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-emerald-100 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Faisal Hills
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">Islamabad</span>
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Premier residential society by Faisal Town Group featuring 11,823 kanals of modern living
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <a
                href="https://wa.me/923335997663"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg flex items-center justify-center"
              >
                Get Expert Consultation
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              Why Choose Faisal Hills?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover the perfect blend of modern amenities, strategic location, and secure investment
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {highlights.map((highlight, index) => {
              const Icon = highlight.icon;
              return (
                <motion.div
                  key={highlight.title}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{highlight.title}</h3>
                  <p className="text-gray-600">{highlight.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blocks Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              Residential & Commercial Blocks
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from diverse blocks designed to meet every lifestyle and investment need
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {blocks.map((block, index) => (
              <motion.div
                key={block.name}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className={`h-2 bg-gradient-to-r ${block.gradient}`} />
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">{block.name}</h3>
                    <span className={`px-3 py-1 bg-gradient-to-r ${block.gradient} text-white text-sm rounded-full`}>
                      {block.type}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6">{block.description}</p>
                  <div className="space-y-3">
                    {block.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <button className="mt-6 w-full py-3 bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-emerald-700 transition-all duration-300">
                    Learn More About {block.name}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              World-Class Amenities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience modern living with comprehensive facilities and services
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {amenities.map((amenity, index) => {
              const Icon = amenity.icon;
              return (
                <motion.div
                  key={amenity.title}
                  className="text-center"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{amenity.title}</h3>
                  <p className="text-gray-600">{amenity.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              Prime Location & Connectivity
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategically located with excellent access to major cities and landmarks
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Nearby Landmarks</h3>
              <div className="space-y-4">
                {nearbyLandmarks.map((landmark, index) => (
                  <motion.div
                    key={landmark.name}
                    className="flex items-center justify-between p-4 bg-white rounded-xl shadow-md"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-semibold text-gray-900">{landmark.name}</p>
                        <p className="text-sm text-gray-600">{landmark.type}</p>
                      </div>
                    </div>
                    <span className="text-blue-600 font-semibold">{landmark.distance}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Accessibility Points</h3>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">N-125 National Highway</span>
                    <span className="text-green-600 font-semibold">5 minutes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Quaid Avenue</span>
                    <span className="text-green-600 font-semibold">5 minutes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Tarnol Morr</span>
                    <span className="text-green-600 font-semibold">10 minutes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">M-1 Motorway</span>
                    <span className="text-green-600 font-semibold">22 minutes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Srinagar Highway</span>
                    <span className="text-green-600 font-semibold">22 minutes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Islamabad Airport</span>
                    <span className="text-green-600 font-semibold">25 minutes</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              Ready to Invest in Your Future?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Our expert team is here to guide you through every step of your property investment journey
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+923335997663"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-emerald-700 transition-all duration-300 shadow-lg flex items-center justify-center"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Ali Raza Shah
              </a>
              <a
                href="https://wa.me/923335997663"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg flex items-center justify-center"
              >
                WhatsApp Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FaisalHills;
